<template>
  <label class="font-bold text-lg">{{ label }}</label>
  <main class="image-picker w-full mt-3">
    <label v-if="modelValue.length === 0" class="drop-zone">
      <input
        type="file"
        @change.prevent="onFileChange"
        :accept="accept"
        hidden
        multiple
      />
      <div class="content-images">
        <v-icon
          name="md-uploadfile-outlined"
          class="text-h-primary-500"
          :scale="2"
        />
        <p class="mt-3">{{ $t("theme.org.form.imagePicker") }}</p>
      </div>
    </label>

    <div v-else class="preview-container w-full">
      <div v-for="(src, index) in modelValue" :key="index" class="preview">
        <img
          :src="getFileUrl(src)"
          :alt="src.name"
          class="object-contain bg-neutral-100 border"
        />
        <button text @click.prevent="removeImage(index)">×</button>
      </div>
      <label v-if="modelValue.length < maxFiles">
        <input
          type="file"
          @change.prevent="onFileChange"
          :accept="accept"
          hidden
          multiple
        />
        <v-icon
          name="md-addcircle"
          class="text-h-primary-500 cursor-pointer hover:opacity-75"
          :scale="2"
        />
      </label>
    </div>
  </main>
</template>

<script lang="ts">
import { getFileUrl } from "@/utils/generics.util";
import { defineComponent, type PropType } from "vue";

export default defineComponent({
  name: "ImagePicker",
  props: {
    modelValue: {
      type: Array as PropType<File[]>,
      default: [],
    },
    multiple: Boolean,
    label: String,
    inputId: String,
    accept: { type: String, default: "*" },
    maxFiles: {
      type: Number,
      default: 5,
    },
  },
  setup(props, { emit }) {
    const removedFiles: { inputId: string; image: File }[] = [];

    function onFileChange(event: Event): void {
      const target = event.target as HTMLInputElement;
      if (!target.files) return;

      const files = Array.from(target.files);
      const newImages = [...props.modelValue];

      files.forEach((file) => {
        if (newImages.length < (props.maxFiles || 5)) newImages.push(file);
      });

      emit("update:modelValue", newImages);
    }

    function removeImage(index: number): void {
      const removed = props.modelValue[index];
      removedFiles.push({ inputId: props.inputId!, image: removed });

      const newImages = props.modelValue;
      newImages.splice(index, 1);
      emit("update:modelValue", newImages);

      if ((removed as any).image) emit("update:removedFiles", removedFiles);
    }

    return { onFileChange, removeImage, getFileUrl };
  },
});
</script>

<style>
.image-picker {
  display: flex;
}

.drop-zone {
  width: 100%;
  height: 150px;
  cursor: pointer;
  text-align: center;
  transition: 0.3s;
  @apply bg-white border-2 rounded-lg flex items-center justify-center;
}

.drop-zone:hover {
  background: #edf2f7;
}

.content-images {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #2d3748;
}

.preview-container {
  display: flex;
  flex-wrap: wrap;
  height: 150px;
  gap: 10px;

  @apply border-2 p-5 rounded-lg min-w-[500px] justify-center items-center;
}

.preview {
  position: relative;
  width: 80px;
  height: 80px;
}

.preview img {
  width: 100%;
  height: 100%;
  border-radius: 8px;
}

.preview button {
  position: absolute;
  top: 4px;
  right: 4px;
  background: red;
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  width: 20px;
  height: 20px;
  font-size: 14px;
}
</style>
